import React, { useEffect, useMemo } from "react";
import { Card, Progress, Skeleton, Space, Typography } from "antd";
import "./BillingExemptionCard.scss";

const { Text, Title } = Typography;

function asNumber(value) {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatIls(value) {
  const n = asNumber(value);
  if (n == null) return null;
  try {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${Math.round(n)} ₪`;
  }
}

function normalizedPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    waiverSource: String(raw.waiver_source || "").trim().toLowerCase() || "none",
    salesMonth: raw.sales_month || null,
    appliesToMonth: raw.applies_to_month || null,
    progress: asNumber(raw.monthly_sales_progress_percent),
    currentSales: asNumber(raw.monthly_sales_current_month_ils),
    threshold: asNumber(raw.monthly_sales_threshold_ils),
    amountRemaining: asNumber(raw.amount_remaining_for_waiver_ils),
    nextMonthIsExempt: Boolean(raw.next_month_is_exempt),
    calculationMode: raw.calculation_mode || null,
    calculationVersion: asNumber(raw.calculation_version),
    qualificationHash: raw.qualification_hash || null,
  };
}

export default function BillingExemptionCard({
  payload,
  source = "unknown",
  loading = false,
  title = "התקדמות מכירות חודשית",
  className = "",
}) {
  const model = useMemo(() => normalizedPayload(payload), [payload]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[AGENT_BILLING_EXEMPTION_UI_TRACE]", {
      widget: "billing_exemption_card",
      endpoint: source,
      source,
      sales_month: model?.salesMonth ?? null,
      applies_to_month: model?.appliesToMonth ?? null,
      included_total_ils: model?.currentSales ?? null,
      waiver_source: model?.waiverSource ?? null,
      progress: model?.progress ?? null,
      currentSales: model?.currentSales ?? null,
      threshold: model?.threshold ?? null,
      amountRemaining: model?.amountRemaining ?? null,
      nextMonthIsExempt: model?.nextMonthIsExempt ?? null,
      calculation_mode: model?.calculationMode ?? null,
      calculation_version: model?.calculationVersion ?? null,
      qualification_hash: model?.qualificationHash ?? null,
    });
  }, [model, source]);

  if (!model) {
    if (!loading) return null;
    return (
      <Card className={`billing-exemption-card ${className}`.trim()} title={title}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  const showSalesMeter = model.waiverSource === "none" && model.threshold != null;
  if (!showSalesMeter && model.waiverSource === "none" && model.threshold == null) {
    return null;
  }

  const currentSalesText = formatIls(model.currentSales);
  const thresholdText = formatIls(model.threshold);
  const remainingText = formatIls(model.amountRemaining);
  const progressValue =
    model.progress == null ? 0 : Math.max(0, Math.min(100, model.progress));

  let headline = "סטטוס זכאות לחודש הבא";
  let subtitle = null;

  if (model.waiverSource === "referrals") {
    headline = "זכאות לחודש הבא דרך 3 סוכנים פעילים";
  } else if (model.waiverSource === "sales_threshold") {
    headline = "הגעת ליעד — החודש הבא ללא תשלום";
    subtitle =
      currentSalesText && thresholdText
        ? `${currentSalesText} מתוך ${thresholdText}`
        : null;
  } else if (model.waiverSource === "manual_override") {
    headline = "פטור ידני אושר לחודש הבא";
  } else if (showSalesMeter) {
    headline = model.nextMonthIsExempt
      ? "כל הכבוד! השגת פטור לחודש הבא"
      : "פטור מדמי שימוש לחודש הבא";
    subtitle =
      currentSalesText && thresholdText
        ? `${currentSalesText} מתוך ${thresholdText}`
        : null;
  }

  return (
    <Card className={`billing-exemption-card ${className}`.trim()} title={title}>
      <Space direction="vertical" size={10} style={{ inlineSize: "100%" }}>
        <Title level={5} className="billing-exemption-card__headline">
          {headline}
        </Title>

        {subtitle ? (
          <Text className="billing-exemption-card__subtitle">{subtitle}</Text>
        ) : null}

        {showSalesMeter ? (
          <>
            {remainingText && !model.nextMonthIsExempt ? (
              <Text className="billing-exemption-card__remaining">
                נותרו {remainingText} לפטור
              </Text>
            ) : null}
            <Progress
              percent={progressValue}
              status={model.nextMonthIsExempt ? "success" : "active"}
              strokeColor={model.nextMonthIsExempt ? "#16a34a" : "#2563eb"}
              trailColor="#e2e8f0"
              showInfo
              format={(p) => `${Math.round(p || 0)}%`}
            />
          </>
        ) : null}
      </Space>
    </Card>
  );
}
